import axios, { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from 'axios';

// In production, we use the API URL or fall back to empty string
const backendURL = import.meta.env.VITE_API_URL || '';
console.log('API Configuration:', {
  backendURL,
  isDev: import.meta.env.DEV,
  viteApiUrl: import.meta.env.VITE_API_URL
});

const api = axios.create({
  baseURL: backendURL,
  headers: {
    'Content-Type': 'application/json',
  },
  validateStatus: (status) => {
    return status >= 200 && status < 300;
  },
});

// Add request interceptor to set auth header
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      if (!config.headers) {
        config.headers = new AxiosHeaders();
      }
      config.headers.set('Authorization', `Bearer ${token}`);
    }

    // Log request details in development
    console.log('API Request:', {
      url: config.url,
      baseURL: config.baseURL,
      method: config.method,
      headers: config.headers
    });

    return config;
  },
  (error) => Promise.reject(error)
);

// Axios response interceptor: Handle 401/403 errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Enhanced error logging
    console.log("API Error Response:", {
      status: error.response?.status,
      originalUrl: originalRequest.url,
      hasRefreshToken: !!localStorage.getItem('refreshToken'),
      isRetry: !!originalRequest._retry,
      baseURL: backendURL,
      error: error.message,
      response: error.response?.data
    });

    // Handle both 401 and 403 errors
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      console.log("Attempting token refresh...");
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await api.post('/api/auth/refresh', { refreshToken });
        const { accessToken: newAccessToken } = response.data;

        console.log("Token refresh successful");

        localStorage.setItem('accessToken', newAccessToken);
        if (!originalRequest.headers) {
          originalRequest.headers = new AxiosHeaders();
        }
        originalRequest.headers.set('Authorization', `Bearer ${newAccessToken}`);

        return api(originalRequest);
      } catch (err) {
        console.log("Token refresh failed:", err);
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('accessToken');
        window.location.href = '/login';
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

export default api;